/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ColumnDef,
  ColumnFilter,
  getFilteredRowModel,
  GroupingState,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import deepmerge from 'deepmerge';
import { concat, get, isEqual, uniqBy } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useDebugValue, useEffect, useMemo, useRef } from 'react';

import {
  CustomFilterMapping,
  TableRowDisplayModifiers,
  useDefaultReactTableOptions,
  usePrepareFilters,
  useQueryTableDataV8,
} from '@work4all/components';
import { useDateSectionsV8 } from '@work4all/components/lib/dataDisplay/basic-table/hooks/use-date-sections-v8';
import { SELECTION_COLUMN_ID } from '@work4all/components/lib/dataDisplay/basic-table/utils/makeRowsSelectable';

import {
  useDataMutation,
  useRefetchOnEntityChanges,
  useUser,
} from '@work4all/data';
import { useDisableApolloAutoInvalidation } from '@work4all/data//lib/apollo-extras/use-disable-apollo-auto-invalidation';
import { useCustomFieldsConfigContext } from '@work4all/data/lib/custom-fields';
import { useEntityEvents } from '@work4all/data/lib/entity-events/use-entity-events';

import { Work4AllEntity } from '@work4all/models/lib/additionalEnums/Work4AllEntity.entity';
import { entityDefinition } from '@work4all/models/lib/Classes/entityDefinitions';
import {
  DataRequest,
  Filters,
  SortDirection,
  SortOption,
} from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import {
  IColumnNumberFilterConfig,
  ICustomCellConfigBase,
} from '@work4all/models/lib/table-schema/table-schema';

import { viewEntityToMutationEntity } from '@work4all/utils';
import { useDebounce } from '@work4all/utils/lib/hooks/use-debounce';
import { MarkSectionsByDateOptions } from '@work4all/utils/lib/mark-sections-by-date';

import { ObjectLockColumn, SelectionColumn } from './common-table-columns-v8';
import { OBJECT_LOCK_COLUMN_ID } from './generic-column/GenericColumns';
import { DataTableColumnConfig } from './table/DataTableColumnConfig';
import { useColumnDefs } from './table/hooks/use-column-defs';
import { useDataTableState } from './table/hooks/use-data-table-state';
import { useTableColumnsV8 } from './table/hooks/use-table-columns-v8';
import { useTableColumns } from './use-table-Columns';
import {
  isReadStatusModifier,
  IUseTableConfigOptions,
  useTableConfig,
} from './use-table-config';
import { useTableFooterData } from './use-table-footer-data';

export const DATA_TABLE_AUTO_COLUMNS = [
  SELECTION_COLUMN_ID,
  OBJECT_LOCK_COLUMN_ID,
];

export interface IUseDataTableV8Options<
  Entity,
  CustomColumns extends Record<string, ICustomCellConfigBase>
> extends Omit<
      IUseTableConfigOptions<CustomColumns>,
      'layout' | 'customFields'
    >,
    Pick<
      MarkSectionsByDateOptions<unknown, unknown>,
      'availableDateSections' | 'markWithDayNameForPastDaysThisWeek'
    > {
  layout?: IUseTableConfigOptions<CustomColumns>['layout'];
  prefilter?: Filters;
  /**
   * Array of default sort definitions applied when the table loads.
   * Automatically removed if the user applies their own sort.
   */
  defaultSort?: SortOption[];
  defaultColumnFilters?: ColumnFilter[];
  defaultGrouping?: GroupingState;
  forceRequestFields?: Work4AllEntity;
  customFilterMapping?: CustomFilterMapping;
  additionalColumns?: DataTableColumnConfig[];
  /**
   * If set to true, will load the footer data for supported column types.
   *
   * @default false
   */
  enableFooter?: boolean;
  /**
   * If set to true, enables client-side filtering instead of server-side filtering.
   * All data will be loaded from the server and filtering will be handled by TanStack Table.
   *
   * @default false
   */
  enableLocalFiltering?: boolean;
  /**
   * We use that with Entities that we can't query directly
   * e.g. `RaPayment` you can query it from the `RaViewModel`.
   *
   * Full Context: https://work4all.slack.com/archives/C03LXTT1RL4/p1726658196853819
   */
  innerEntityQuery?: {
    parentEntity: Entities;
    fieldName: string;
  };
  pageSize?: number;
  skipSyncBag?: boolean;
  listEntityType?: Entities;
  rowModifiers?: (value?: any) => Record<string, boolean>;
  onRowsSelected?: (rows: Row<Entity>[]) => void;
  enableMultiRowSelection?: boolean;
  enableURLState?: boolean;
  forceSelectionColumn?: boolean;
  /**
   * Array of secondary sort definitions applied after the primary sort for tie-breaking (e.g., by 'number' descending).
   * If the user sorts by a column listed here, the user-selected direction is used instead.
   */
  secondarySort?: {
    field: string;
    direction: SortDirection;
  }[];
  /**
   * Custom sorting function to sort the data based on a custom mapped field (using transform function)
   */
  customSort?: {
    field: string;
    transform: (
      value: unknown
    ) => string | number | DateTime<true> | DateTime<false> | boolean;
    direction: SortDirection;
  };
  vars?: DataRequest['vars'];
  /**
   * If set to true, skips the data request and shows an empty list.
   * Useful for cases where you want to display an empty table without making a request.
   */
  skip?: boolean;
}

const NO_ROWS = [];
const EMPTY_ARRAY = [];

export function useDataTableV8<
  Entity extends Work4AllEntity,
  CustomColumns extends Record<string, ICustomCellConfigBase>
>(options: IUseDataTableV8Options<Entity, CustomColumns>) {
  const {
    enableURLState = true,
    layout = 'table',
    schema,
    prefilter,
    defaultSort = null,
    defaultColumnFilters,
    defaultGrouping = [],
    cells,
    cellProcessors,
    forceRequestFields,
    customFilterMapping,
    additionalColumns,
    enableFooter = false,
    enableLocalFiltering = false,
    innerEntityQuery,
    groupLabelElements,
    pageSize = 100,
    onRowsSelected,
    enableMultiRowSelection = true,
    availableDateSections,
    markWithDayNameForPastDaysThisWeek,
    forceSelectionColumn = false,
    vars,
    skip = false,
  } = options;

  const entityType = innerEntityQuery?.parentEntity ?? schema.entity;

  useDisableApolloAutoInvalidation();

  const { benutzerCode: userId } = useUser();

  const { customFields } = useCustomFieldsConfigContext();

  const {
    fields: extractedFields,
    columnConfigs,
    cardConfig,
    prepareRowDisplayModifiers,
  } = useTableConfig({
    layout,
    schema,
    customFields,
    cells,
    cellProcessors,
    additionalColumns,
    groupLabelElements,
  });

  const dataTableState = useDataTableState({
    columnConfigs,
    autoColumns: DATA_TABLE_AUTO_COLUMNS,
    enableURLState,
    entityType,
    layout,
    defaultSort,
    defaultColumnFilters,
    defaultGrouping,
    isResizing: () => {
      return table.getAllFlatColumns().some((c) => c.getIsResizing());
    },
  });

  const state = dataTableState.state;

  const { filter, sort } = usePrepareFilters(
    enableLocalFiltering ? EMPTY_ARRAY : state.columnFilters,
    state.sorting,
    customFilterMapping,
    entityType
  );

  const rowModifiers = useRef(options.rowModifiers).current;

  const prepareAllRowModifiers = useCallback<
    (row: unknown) => TableRowDisplayModifiers
  >(
    (value: unknown) => {
      const defaultModifiers = prepareRowDisplayModifiers?.(value);
      const extraModifiers = rowModifiers?.(value);

      return {
        ...defaultModifiers,
        ...extraModifiers,
      };
    },
    [rowModifiers, prepareRowDisplayModifiers]
  );

  const fields = useMemo(() => {
    //eslint-disable-next-line
    //@ts-ignore
    const mergedFields = deepmerge(extractedFields, forceRequestFields || {});

    if (innerEntityQuery) {
      return { id: null, [innerEntityQuery.fieldName]: [mergedFields] };
    }

    return mergedFields;
  }, [extractedFields, forceRequestFields, innerEntityQuery]);

  // TODO Replace with transition when suspense is available in data provider
  const debouncedGlobalFilter = useDebounce(state.globalFilter, 200);

  const searchFilter: Filters = useMemo(() => {
    if (!debouncedGlobalFilter || enableLocalFiltering) {
      return [];
    } else {
      const filter = [];

      schema.columns.forEach((col) => {
        const isNumber =
          (col.filterable as IColumnNumberFilterConfig)?.type === 'Number';

        if (col.quickSearchable) {
          if (!isNumber || parseInt(debouncedGlobalFilter))
            filter.push({
              [col.accessor]: {
                $eq: isNumber
                  ? `%${parseInt(debouncedGlobalFilter)}%`
                  : `%${debouncedGlobalFilter.trim()}%`,
              },
            });
        }
      });
      return filter;
    }
  }, [schema.columns, debouncedGlobalFilter, enableLocalFiltering]);

  const finalFilter = useMemo(() => {
    const finalFilter = concat(...[filter, prefilter].filter(Boolean));

    if (searchFilter.length) {
      finalFilter.push({
        $or: searchFilter,
      });
    }

    return finalFilter;
  }, [filter, prefilter, searchFilter]);

  const requestData = useMemo(() => {
    const completeDataResponse =
      entityDefinition[entityType]?.remote?.withPaginationWrapper === false;

    function getSortOptions() {
      const sortOptions = sort.map((option) => {
        const column = schema.columns.find((column) => column.id === option.id);

        return {
          // sortColumn?.accessor is not defined when prefilter is made on column that is not schema
          field: column?.accessor ?? option.id,
          direction: option.desc
            ? SortDirection.DESCENDING
            : SortDirection.ASCENDING,
        };
      });

      if (options.secondarySort) {
        const cleandedSecondarySort = options.secondarySort.filter(
          (sort) => !sortOptions.find((option) => option.field === sort.field)
        );

        return [...sortOptions, ...cleandedSecondarySort];
      }

      return sortOptions;
    }

    const request: DataRequest = {
      filter: finalFilter.length > 0 ? finalFilter : undefined,
      entity: entityType as Entities,
      completeDataResponse,
      data: fields,
      sort: getSortOptions(),
      vars,
      skip,
    };

    return request;
  }, [
    entityType,
    finalFilter,
    fields,
    sort,
    options.secondarySort,
    schema.columns,
    vars,
    skip,
  ]);

  const footerData = useTableFooterData({
    enabled: enableFooter,
    entity: schema.entity as Entities,
    columns: columnConfigs,
    filter: finalFilter,
  });

  const allColumns = useTableColumns<CustomColumns>(
    schema.columns,
    customFields
  );

  const getGroupKeyFields = useCallback(
    (columnId: string) => {
      const column = allColumns.find((column) => {
        const id = column.id ?? column.accessor;
        return id === columnId;
      });

      if (!column) {
        throw new Error(`Could not find a column with id "${columnId}".`);
      }

      const { groupField, accessor, id = accessor } = column;

      return {
        id: id as string,
        label: groupField
          ? groupField
          : typeof accessor === 'string'
          ? accessor
          : null,
      };
    },
    [allColumns]
  );

  const tableDataColumns = useMemo(() => {
    return columnConfigs
      .filter((column) => !column.disableFooterSum && column.Footer != null)
      .map((column) => column.accessor as string);
  }, [columnConfigs]);

  const columnsCache = useTableColumnsV8({
    columnConfigs,
    listEntityType: options.listEntityType,
  });

  const columnDefs = useColumnDefs<Entity>(columnsCache.columns);
  const columns = useMemo<ColumnDef<Entity, any>[]>(() => {
    return uniqBy([SelectionColumn, ObjectLockColumn, ...columnDefs], 'id');
  }, [columnDefs]);

  const table = useReactTable<Entity>({
    ...useDefaultReactTableOptions(),
    ...dataTableState,
    ...(enableLocalFiltering && { getFilteredRowModel: getFilteredRowModel() }),
    columns: columns,
    data: NO_ROWS,
    enableMultiRowSelection: enableMultiRowSelection,
    entityType: entityType,
    prepareRowDisplayModifiers: prepareAllRowModifiers,
    footerData: footerData,
  });

  const tableData = useQueryTableDataV8(
    table,
    columnConfigs,
    requestData,
    getGroupKeyFields,
    pageSize,
    customFields,
    tableDataColumns,
    enableFooter
  );

  const innerQueryTableData = useMemo(() => {
    if (!innerEntityQuery) return tableData;

    const data = tableData?.data?.[0]?.[innerEntityQuery.fieldName] ?? [];

    return {
      ...tableData,
      data,
      total: data.length,
    };
  }, [innerEntityQuery, tableData]);

  const data = useDateSectionsV8({
    columns: columns,
    data: innerQueryTableData.data,
    sorting: state.sorting,
    grouping: state.grouping,
    availableDateSections,
    markWithDayNameForPastDaysThisWeek,
  });

  const customSortedData = !options.customSort
    ? data
    : data.sort((a, b) => {
        const valA = options.customSort.transform(a[options.customSort.field]);
        const valB = options.customSort.transform(b[options.customSort.field]);

        let comparison = 0;

        if (typeof valA === 'string' && typeof valB === 'string') {
          comparison = valA.localeCompare(valB);
        } else if (typeof valA === 'number' && typeof valB === 'number') {
          comparison = valA - valB;
        }

        return options.customSort.direction === SortDirection.ASCENDING
          ? comparison
          : -comparison;
      });

  table.setOptions((prev) => ({
    ...prev,
    data: customSortedData as Entity[],
  }));

  const selectedRowsCount = Object.entries(state.rowSelection).reduce(
    (acc, [, value]) => (value ? acc + 1 : acc),
    0
  );

  table.setOptions((prev) => ({
    ...prev,
    state: {
      ...prev.state,
      columnVisibility: {
        ...prev.state.columnVisibility,
        ...Object.fromEntries(state.grouping.map((col) => [col, false])),
        [SELECTION_COLUMN_ID]: forceSelectionColumn || selectedRowsCount > 1,
        [OBJECT_LOCK_COLUMN_ID]: table
          .getCoreRowModel()
          .flatRows.some((row) => !!(row.original as any).objectLock),
      },
    },
  }));

  const mutationEntity = viewEntityToMutationEntity(entityType);
  useRefetchOnEntityChanges({
    entity: mutationEntity,
    refetch: innerQueryTableData.refetch,
  });

  useEntityEvents((event) => {
    if (event.entity === Entities.favoriteItem) {
      innerQueryTableData.refetch();
    }
  });

  const readStatusModifier = useMemo(() => {
    const modifiers = schema.rowModifiers.filter(isReadStatusModifier);

    if (modifiers.length > 1) {
      console.warn(
        'Multiple ReadStatus modifiers found.' +
          ' This is a misconfiguration in the table schema.' +
          ' Only the first one will be used.'
      );
    }

    if (modifiers.length > 0) {
      return modifiers[0];
    } else {
      return null;
    }
  }, [schema]);

  const selectedFlatRows = table.getSelectedRowModel().flatRows;
  const selectedEntities = useMemo(() => {
    return selectedFlatRows.map((row) => row.original) as Entity[];
  }, [selectedFlatRows]);
  const selectedEntity =
    selectedEntities.length === 1 ? selectedEntities[0] : null;

  const responseData = useMemo(
    () =>
      readStatusModifier
        ? {
            id: null,
            [readStatusModifier.params.field]: null,
          }
        : {},
    [readStatusModifier]
  );

  const [mutateEntity] = useDataMutation({
    entity: mutationEntity,
    mutationType: EMode.upsert,
    responseData,
  });

  // This effect calls onRowsSelected whenever the selected rows change
  // This works even when the selection state hasn't changed but items are no longer present in the list
  const prevSelectedRowsRef = useRef<unknown[]>([]);
  useEffect(() => {
    if (onRowsSelected) {
      const selectedRows = selectedFlatRows.map((row) => row.original);
      if (!isEqual(selectedRows, prevSelectedRowsRef.current)) {
        prevSelectedRowsRef.current = selectedRows;
        onRowsSelected(selectedFlatRows);
      }
    }
  }, [selectedFlatRows, onRowsSelected]);

  useEffect(() => {
    if (
      readStatusModifier !== null &&
      selectedEntity !== null &&
      get(selectedEntity, readStatusModifier.params.field) === false &&
      get(selectedEntity, readStatusModifier.params.ownerIdField) === userId &&
      'id' in selectedEntity
    ) {
      mutateEntity({
        id: selectedEntity.id,
        [readStatusModifier.params.field]: true,
      });
    }
  }, [mutateEntity, selectedEntity, userId, readStatusModifier]);

  useDebugValue({
    ...innerQueryTableData,
    columnConfigs,
    cardConfig,
    selectedEntity,
  });

  return {
    ...innerQueryTableData,
    columnConfigs,
    cardConfig,
    selectedEntity,
    selectedEntities,
    entityType: schema.entity,
    table,
  };
}
